"use client";

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../UI/Button/Button";
import Card from "../UI/Card/Card";
import Tabs from "../UI/Tabs/Tabs";
import Alert from "../UI/Alert/Alert";
import { fetchPatientData, mapPatientData } from "../../services/data-service";
import FieldMappingVisualizer from "../FieldMappingVisualizer/FieldMappingVisualizer";
import MappingSummary from "../MappingSummary/MappingSummary";
import Loader from "../UI/Loader/Loader";
import "./MappingPage.css";

function MappingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const uhid = searchParams.get("uhid");

  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [mappedData, setMappedData] = useState(null);
  const [mappingPaths, setMappingPaths] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("mapping");

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const data = await fetchPatientData(uhid);
        console.log(data, "patient data check");

        setPatientData(data);

        const { mappedData, mappingPaths } = await mapPatientData(data);
        setMappedData(mappedData);
        setMappingPaths(mappingPaths);

        // Set default active field
        if (Object.keys(mappedData).length > 0) {
          setActiveField(Object.keys(mappedData)[0]);
        }
      } catch (err) {
        console.error("Data processing error:", err);
        setError("No Data Found");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [uhid]);

  const handleContinue = () => {
    navigate(`/review?uhid=${uhid}`);
  };

  const handleFieldSelect = (field) => {
    setActiveField(field);
  };

  if (loading) {
    return (
      <div className="mapping-container">
        <div className="loading-wrapper">
          <Card className="loading-card">
            <div className="loading-content">
              <Loader className="loader-animation" />
              <p className="loading-text">Processing patient data...</p>
              <div className="loading-progress">
                <div className="progress-bar"></div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mapping-container">
        <div className="error-wrapper slide-up">
          <Card className="error-card">
            <div className="error-content">
              <Alert variant="destructive" title="Error" description={error} />
              <div className="error-actions">
                <Button onClick={() => navigate("/home")}>
                  Return to Home
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="mapping-container">
      <div className="mapping-content slide-up">
        <Card className="mapping-card">
          <div className="card-header">
            <h2 className="card-title">
              EPIC EMR Data Mapping Process for AICVD Model
            </h2>
            <p className="card-description">
              Visualizing how patient data is mapped to AICVD model fields
            </p>
          </div>
          <div className="card-content">
            <Tabs
              tabs={[
                { id: "mapping", label: "Field Mapping" },
                { id: "summary", label: "Mapping Summary" },
                { id: "raw", label: "Raw Patient Data" },
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
            >
              {activeTab === "mapping" && mappedData && mappingPaths && (
                <FieldMappingVisualizer
                  patientData={patientData}
                  mappedData={mappedData}
                  mappingPaths={mappingPaths}
                  activeField={activeField}
                  onFieldSelect={handleFieldSelect}
                />
              )}

              {activeTab === "summary" && mappedData && mappingPaths && (
                <MappingSummary
                  mappedData={mappedData}
                  mappingPaths={mappingPaths}
                />
              )}

              {activeTab === "raw" && (
                <div className="raw-data-container">
                  <pre className="raw-data-content">
                    {JSON.stringify(patientData, null, 2)}
                  </pre>
                </div>
              )}
            </Tabs>
          </div>
          <div className="card-footer">
            <Button variant="outline" onClick={() => navigate("/home")}>
              Back to Home
            </Button>
            <Button onClick={handleContinue} className="continue-button">
              Continue to Review
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default MappingPage;
